import React, { useState, useEffect, useRef, Dispatch, useMemo } from 'react';
import { FlatList, ScrollView, View } from 'react-native';
import { connect, RootStateOrAny, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import moment, { DurationInputArg1, Moment } from 'moment';

import { isPhone, isWeb, localTime } from '@helpers';
import { scale, width } from '@components/frame';

import ColorWrapper, { WithColor } from '@styles/colors';
import { getBoxShadowStyle } from '@styles/shadow';

import { ncFileStatisticsPaid, machineStatisticsPaid } from '@reducers/statistics';
import { useHandleNavigationMachinesHeader } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';

import BarChart from './components/barChart/index';
import Arrow from './components/arrow/index';

import { getBarLegend } from '@components/machines/components/eventsChart/calculation';
import { getPeriodStep } from '@components/machines/components/eventsChart/helpers/period';
import { useMobileChartReset } from '@components/machines/components/eventsChart/hooks/useMobileChartReset';

import Histogram from '@v2/elements/Histogram';
import { InlineErrorBoundary } from '@v2/components/errorBoundary';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import {
  PERFORMANCE_CHART_BLOCK_WIDTH,
  SCREEN_BORDER_RADIUS,
  SCREEN_PADDING_HORIZONTAL,
  SCREEN_PADDING_HORIZONTAL_FOR_ITEM,
} from '../../../v2/pages/common/constants/base.constants';
import { selectNcFileId } from '@reducers/details';
import { selectSelectedItem } from '@reducers/machines';
import { KEYS_TAGS } from '@components/imageHandling/common';

export interface StatisticsPerPeriod {
  height?: number;
  totalProductiveSecs: number;
  averageActiveSecs: number;
  averageChangeSecs: number;
  averageSetupSecs: number;
  loaded?: boolean;
  dateTime?: string;
  quantity?: number;
  productionBatchCount?: number;
}

export interface StatisticsData {
  statisticsPerPeriod: StatisticsPerPeriod[];
  statisticsOverall?: any;
}

export interface RenderChartData {
  loaded: boolean;
  error: boolean;
  data: StatisticsData;
}

export type RenderChart = {
  date: string;
  start: boolean | undefined;
  data: RenderChartData;
} | null;

const Paid = (props: WithColor & {
  selectedChartDate: string;
  id: number;
  ncFileStatisticsPaid: ThunkAction<Promise<void>, RootStateOrAny, unknown, Action>;
  machineStatisticsPaid: ThunkAction<Promise<void>, RootStateOrAny, unknown, Action>;
  setSelectedChartDate: Dispatch<string>;
  featureFlags: any;
}) => {
  const { C, selectedChartDate, ncFileStatisticsPaid, machineStatisticsPaid, setSelectedChartDate, featureFlags } = props;

  const ncFileId = useSelector(selectNcFileId);
  const selectedMachine = useSelector(selectSelectedItem);

  const { statisticsScope } = useHandleNavigationMachinesHeader();
  const { timePeriod: selectedTime, id: machineId } = useHandleParams();

  // TODO: machineStatisticsPaid - need to verify when backend is populated, now always 0 (/api/v2/statistics/machineStatisticsPaid)
  const statsConfig = {
    [KEYS_TAGS.Machine]: { getData: machineStatisticsPaid, id: machineId, idKey: 'machineId' },
    [KEYS_TAGS.Clamping]: { getData: ncFileStatisticsPaid, id: ncFileId ?? selectedMachine?.machineStatus?.ncFileId, idKey: 'ncFileId' },
  };
  const { getData, id, idKey } = statsConfig[statisticsScope];
  const { IS_NARROW_WEB } = useDeviceSize();

  const flatListRef = useRef<FlatList<any>>();

  const selectedDate = useMemo(() => {
    if (selectedChartDate) {
      const dateString = moment(selectedChartDate).format('YYYY-MM-DD');
      return moment.utc(dateString).format();
    } else {
      const dateString = moment().format('YYYY-MM-DD');
      return moment.utc(dateString).format();
    }
  }, [selectedChartDate]);

  const blockWidth =
    isPhone && !IS_NARROW_WEB ? width - scale(SCREEN_PADDING_HORIZONTAL) * 2 : scale(PERFORMANCE_CHART_BLOCK_WIDTH) + SCREEN_PADDING_HORIZONTAL_FOR_ITEM * 2;
  const padding = scale(SCREEN_PADDING_HORIZONTAL);
  const elementWidth = blockWidth - padding * 2;
  const chartWidth = blockWidth - padding * 2 - 30;

  const { legend = [] } = getBarLegend(selectedTime, selectedDate);
  const barAmount = legend.length;

  // Moment-compatible unit + step count for the active period — handles 'eightWeeks' (8 ISO weeks).
  const periodStep = getPeriodStep(selectedTime);

  const resetData = () => {
    const { legend = [] } = getBarLegend(selectedTime, selectedDate);
    const barAmount = legend.length;

    const newData = [
      getDate({ initial: selectedDate, action: 'add', amount: 2, length: barAmount }),
      getDate({ initial: selectedDate, action: 'add', amount: 1, length: barAmount }),
      getDate({ initial: selectedDate, start: true, length: barAmount }),
      getDate({ initial: selectedDate, action: 'subtract', amount: 1, length: barAmount }),
    ].filter((i) => i);

    setRenderChart(newData);

    const newIndex = newData.findIndex((i) => i?.start);
    setSelectedIndex(newIndex);

    if (!isWeb && flatListRef.current) {
      setTimeout(() => {
        if (flatListRef?.current) {
          flatListRef.current.scrollToIndex({
            index: newIndex,
            animated: false,
          });
        }
      }, 0);
    }
  };

  const { resetMobileChartCount, setSelectedChartDateWithReset: setDate } = useMobileChartReset((d) => setSelectedChartDate(moment(d).endOf('day').format()));

  useEffect(() => {
    if (isWeb) {
      resetData();
    }
  }, [selectedDate, id, selectedTime]);

  useEffect(() => {
    if (!isWeb) {
      resetData();
    }
  }, [id, selectedTime, resetMobileChartCount]);

  const getDate = ({
    initial,
    action,
    amount,
    start,
    length = barAmount,
  }: {
    initial?: string;
    action?: 'subtract' | 'add';
    amount?: DurationInputArg1;
    start?: boolean;
    length?: number;
  }) => {
    let date: Moment | string = moment.utc(initial);
    if (action) {
      const stepCount = (Number(amount) || 0) * periodStep.count;
      switch (action) {
        case 'subtract': {
          (date as Moment).subtract(stepCount, periodStep.unit);
          break;
        }
        case 'add': {
          (date as Moment).add(stepCount, periodStep.unit);
          break;
        }
      }
    }

    date = date.format('YYYY-MM-DD');

    const pageEnd = moment.utc(date).startOf(periodStep.unit).add(periodStep.count, periodStep.unit);
    const nowPageEnd = moment.utc().startOf(periodStep.unit).add(periodStep.count, periodStep.unit);
    if (pageEnd.isAfter(nowPageEnd)) {
      return null;
    }

    return {
      date,
      start,
      data: {
        loaded: false,
        error: false,
        data: {
          statisticsPerPeriod: Array.from({ length: barAmount }, () => ({
            totalProductiveSecs: Math.random() * 100,
            averageActiveSecs: Math.random() * 100,
            averageChangeSecs: Math.random() * 100,
            averageSetupSecs: Math.random() * 100,
          })),
        },
      },
    };
  };

  const [renderChart, setRenderChart] = useState<RenderChart[]>(
    [
      getDate({ initial: selectedDate, action: 'add', amount: 2 }),
      getDate({ initial: selectedDate, action: 'add', amount: 1 }),
      getDate({ initial: selectedDate, start: true }),
      getDate({ initial: selectedDate, action: 'subtract', amount: 1 }),
    ].filter((i) => i)
  );

  const [selectedIndex, setSelectedIndex] = useState(renderChart.findIndex((i) => i?.start));

  const saveChartData = (date: Moment | string, data: RenderChart) => {
    setRenderChart((currentValues: any) => currentValues.map((i: any) => (i.date === date ? { ...i, data } : i)));
  };

  const loadPastData = () => {
    const date = getDate({ initial: renderChart[renderChart.length - 1]?.date, action: 'subtract', amount: 1 });
    if (date) {
      setRenderChart([...renderChart, date]);
    }
  };

  const loadFeatureData = () => {
    const date = getDate({ initial: renderChart[0]?.date, action: 'add', amount: 1 });
    if (date) {
      setRenderChart([date, ...renderChart]);
    }
  };

  const userStopScroll = (number: number) => {
    const i = Math.floor(number / chartWidth);
    const date = renderChart[i]?.date;
    const pageEnd = moment.utc(date).startOf(periodStep.unit).add(periodStep.count, periodStep.unit);
    const nowPageEnd = moment.utc().startOf(periodStep.unit).add(periodStep.count, periodStep.unit);
    const isEnd = pageEnd.isSameOrAfter(nowPageEnd);
    const newIndex = isEnd ? i : i || 1;
    setSelectedIndex(newIndex);

    if (date) {
      setSelectedChartDate(date);
    }
    if (i == 0) {
      loadFeatureData();
    }
    if (i >= renderChart.length - 2) {
      loadPastData();
    }

    setTimeout(() => {
      if (flatListRef.current) {
        flatListRef.current.scrollToIndex({
          index: newIndex,
          animated: false,
        });
      }
    }, 0);
  };

  const selectedItem = renderChart[selectedIndex];
  const statisticsOverall = selectedItem?.data?.data?.statisticsOverall || {};

  const showPercentilesLineChart = featureFlags['ncfile-showpercentiles'];

  const isBatchSizeValid = statisticsOverall.productionBatchCount >= 3;
  const isQuantityValid = statisticsOverall.quantity >= 3;

  const shadowStyles = getBoxShadowStyle({ type: 'Narrow', C });

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <View style={{ width: blockWidth, paddingVertical: SCREEN_PADDING_HORIZONTAL_FOR_ITEM, marginHorizontal: 'auto', flex: 1 }}>
        <View
          style={[
            isWeb && shadowStyles,
            {
              marginHorizontal: 'auto',
              width: blockWidth - SCREEN_PADDING_HORIZONTAL_FOR_ITEM * 2,
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: C('Background/Light/Field'),
            },
          ]}
        >
          <View style={{ width: '100%' }}>
            {/* Total part */}
            <View style={{ position: 'absolute', left: -50, top: 122, zIndex: 1000 }}>
              <Arrow
                {...{
                  C,
                  onPress: () => setDate(moment(selectedDate).subtract(periodStep.count, periodStep.unit).format()),
                }}
              />
            </View>
            <InlineErrorBoundary screenName="PaidTotalChartBlock">
              <BarChart
                {...{
                  barAmount,
                  selectedDate,
                  padding,
                  chartWidth,
                  getData,
                  idKey,
                  average: statisticsOverall.totalProductiveSecs,
                  averagePaid: statisticsOverall.totalPaidProductiveSecs,
                  barValueName: 'totalProductiveSecs',
                  lineValueName: 'totalPaidProductiveSecs',
                  lineText: 'Paid Productive',
                  barText: 'Productive',
                  lineColor: 'Status/Light/Paid',
                  barColor: 'Status/Light/Productive',
                  id,
                  renderChart,
                  selectedIndex,
                  saveChartData,
                  showAverage: false,
                  userStopScroll,
                  flatListRef,
                }}
              />
            </InlineErrorBoundary>
            {moment(selectedDate)
              .startOf(periodStep.unit)
              .add(periodStep.count, periodStep.unit)
              .isSameOrBefore(moment().startOf(periodStep.unit)) && (
              <View style={{ position: 'absolute', right: -50, top: 122, zIndex: 1000, transform: [{ rotate: '180deg' }] }}>
                <Arrow
                  {...{
                    C,
                    onPress: () => setDate(moment(selectedDate).add(periodStep.count, periodStep.unit).format()),
                  }}
                />
              </View>
            )}
            {/*  */}
          </View>
        </View>
        <View
          style={[
            isWeb && shadowStyles,
            {
              width: blockWidth - SCREEN_PADDING_HORIZONTAL_FOR_ITEM * 2,
              marginHorizontal: 'auto',
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 20,
              backgroundColor: C('Background/Light/Field'),
            },
          ]}
        >
          {/* Active part */}
          <View style={{ width: '100%' }}>
            <InlineErrorBoundary screenName="PaidActiveChartBlock">
              <BarChart
                {...{
                  barAmount,
                  selectedDate,
                  padding,
                  chartWidth,
                  quantity: statisticsOverall.quantity,
                  average: statisticsOverall.averageActiveSecs,
                  averagePaid: statisticsOverall.averagePaidActiveSecs,
                  barValueName: 'averageActiveSecs',
                  lineValueName: 'averagePaidActiveSecs',
                  lineText: 'Paid Active',
                  barText: 'Active',
                  lineColor: 'Status/Light/Paid',
                  barColor: 'Status/Light/Active',
                  id,
                  renderChart,
                  selectedIndex,
                  saveChartData,
                  showAverage: true,
                  userStopScroll,
                }}
              />
              <View style={{ width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                <Histogram
                  {...{
                    data: [
                      {
                        name: 'Scheduled',
                        value: statisticsOverall.averageActiveSecs,
                        color: 'Selected/Light/Primary',
                        icon: 'scheduledTime',
                        width: 14,
                        height: 14,
                      },
                      {
                        name: 'Paid',
                        value: statisticsOverall.averagePaidActiveSecs,
                        color: 'Status/Light/Paid',
                        icon: 'euro',
                        width: 10,
                        height: 18,
                      },
                    ],
                    mainColor: 'Status/Light/Active',
                    P25: statisticsOverall.p25ActiveSecs,
                    P50: statisticsOverall.p50ActiveSecs,
                    P75: statisticsOverall.p75ActiveSecs,
                    elementWidth,
                    showPercentiles: showPercentilesLineChart && isQuantityValid,
                    loaded: selectedItem?.data.loaded,
                  }}
                />
              </View>
            </InlineErrorBoundary>
          </View>
          {/*  */}
          {/* Change part */}
          <View style={{ width: '100%' }}>
            <InlineErrorBoundary screenName="PaidChangeChartBlock">
              <BarChart
                {...{
                  barAmount,
                  selectedDate,
                  padding,
                  chartWidth,
                  quantity: statisticsOverall.quantity,
                  average: statisticsOverall.averageChangeSecs,
                  averagePaid: statisticsOverall.averagePaidChangeSecs,
                  barValueName: 'averageChangeSecs',
                  lineValueName: 'averagePaidChangeSecs',
                  lineText: 'Paid Change',
                  barText: 'Change',
                  lineColor: 'Status/Light/Paid',
                  barColor: 'Status/Light/Change',
                  id,
                  renderChart,
                  selectedIndex,
                  saveChartData,
                  showAverage: true,
                  userStopScroll,
                }}
              />
              <View style={{ width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                <Histogram
                  {...{
                    data: [
                      {
                        name: 'Scheduled',
                        value: statisticsOverall.averageChangeSecs,
                        color: 'Selected/Light/Primary',
                        icon: 'scheduledTime',
                        width: 14,
                        height: 14,
                      },
                      {
                        name: 'Paid',
                        value: statisticsOverall.averagePaidChangeSecs,
                        color: 'Status/Light/Paid',
                        icon: 'euro',
                        width: 10,
                        height: 18,
                      },
                    ],
                    mainColor: 'Status/Light/Change',
                    P25: statisticsOverall.p25ChangeSecs,
                    P50: statisticsOverall.p50ChangeSecs,
                    P75: statisticsOverall.p75ChangeSecs,
                    elementWidth,
                    showPercentiles: showPercentilesLineChart && isQuantityValid,
                    loaded: selectedItem?.data.loaded,
                  }}
                />
              </View>
            </InlineErrorBoundary>
          </View>
          {/*  */}
          {/* Setup part */}
          <View style={{ width: '100%' }}>
            <InlineErrorBoundary screenName="PaidSetupChartBlock">
              <BarChart
                {...{
                  barAmount,
                  selectedDate,
                  padding,
                  chartWidth,
                  batches: statisticsOverall.productionBatchCount,
                  average: statisticsOverall.averageSetupSecs,
                  averagePaid: statisticsOverall.averagePaidSetupSecs,
                  barValueName: 'averageSetupSecs',
                  lineValueName: 'averagePaidSetupSecs',
                  lineText: 'Paid Setup',
                  barText: 'Setup',
                  lineColor: 'Status/Light/Paid',
                  barColor: 'Status/Light/Setup',
                  id,
                  renderChart,
                  selectedIndex,
                  saveChartData,
                  showAverage: true,
                  userStopScroll,
                }}
              />
              <View style={{ width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                <Histogram
                  {...{
                    data: [
                      {
                        name: 'Scheduled',
                        value: statisticsOverall.averageSetupSecs,
                        color: 'Selected/Light/Primary',
                        icon: 'scheduledTime',
                        width: 14,
                        height: 14,
                      },
                      {
                        name: 'Paid',
                        value: statisticsOverall.averagePaidSetupSecs,
                        color: 'Status/Light/Paid',
                        icon: 'euro',
                        width: 10,
                        height: 18,
                      },
                    ],
                    mainColor: 'Status/Light/Setup',
                    P25: statisticsOverall.p25SetupSecs,
                    P50: statisticsOverall.p50SetupSecs,
                    P75: statisticsOverall.p75SetupSecs,
                    showPercentiles: showPercentilesLineChart && isBatchSizeValid,
                    elementWidth,
                    loaded: selectedItem?.data.loaded,
                  }}
                />
              </View>
            </InlineErrorBoundary>
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

export default connect(
  ({
    user: { featureFlags },
  }) => ({ featureFlags }),
  {
    ncFileStatisticsPaid,
    machineStatisticsPaid,
  }
)(ColorWrapper(Paid));
